import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip, Button } from "@mui/material";
import { Link } from "react-router-dom";

import StatsCard from "../components/StatsCard";
import ChartArea from "../components/ChartArea";
import ChartBar from "../components/ChartBar";
import ChartPie from "../components/ChartPie";

import { ref, get } from "firebase/database";
import { rtdb } from "../../services/firebase";
import { getOrderStatusLabelPt, getOrderStatusColor, isFinalOrderStatus, normalizeOrderStatus } from "../../utils/orderStatus";

export default function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [productsCount, setProductsCount] = useState(0);
    const [clientsCount, setClientsCount] = useState(0);

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const [ordersSnap, productsSnap, usersSnap] = await Promise.all([
                    get(ref(rtdb, "orders")),
                    get(ref(rtdb, "products")),
                    get(ref(rtdb, "users/client")),
                ]);

                const rawOrders = ordersSnap.exists() ? ordersSnap.val() : {};
                const ordersArr = Object.keys(rawOrders).map((id) => ({
                    id,
                    ...rawOrders[id],
                }));
                setOrders(ordersArr);

                const rawProducts = productsSnap.exists() ? productsSnap.val() : {};
                setProductsCount(Object.keys(rawProducts).length);

                const rawUsers = usersSnap.exists() ? usersSnap.val() : {};
                setClientsCount(Object.keys(rawUsers).length);
            } catch (err) {
                console.error("Erro ao carregar dashboard:", err);
                setOrders([]);
                setProductsCount(0);
                setClientsCount(0);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, []);

    const stats = useMemo(() => {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const endOfDay = startOfDay + 24 * 60 * 60 * 1000;

        const ordersToday = orders.filter((o) => (o.createdAt || 0) >= startOfDay && (o.createdAt || 0) < endOfDay);
        const revenueToday = ordersToday.reduce((sum, o) => sum + Number(o.total || 0), 0);

        const formatBRL = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

        return [
            { label: "Pedidos Hoje", value: ordersToday.length },
            { label: "Faturamento Hoje", value: formatBRL(revenueToday) },
            { label: "Produtos Ativos", value: productsCount },
            { label: "Clientes", value: clientsCount },
        ];
    }, [orders, productsCount, clientsCount]);

    const vendasSemana = useMemo(() => {
        const now = new Date();
        const days = Array.from({ length: 7 }).map((_, i) => {
            const d = new Date(now);
            d.setDate(now.getDate() - (6 - i));
            const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
            const end = start + 24 * 60 * 60 * 1000;
            const total = orders
                .filter((o) => (o.createdAt || 0) >= start && (o.createdAt || 0) < end)
                .reduce((sum, o) => sum + Number(o.total || 0), 0);

            const weekday = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"][d.getDay()];
            return { name: weekday, valor: Math.round(total) };
        });
        return days;
    }, [orders]);

    const produtosVendidos = useMemo(() => {
        const counts = new Map();
        orders.forEach((o) => {
            (o.items || []).forEach((it) => {
                const name = it.productName || it.productId || "Produto";
                counts.set(name, (counts.get(name) || 0) + Number(it.quantity || 0));
            });
        });
        const top = Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4)
            .map(([name, value]) => ({ name, value }));
        return top.length ? top : [{ name: "Sem dados", value: 1 }];
    }, [orders]);

    const faturamentoMes = useMemo(() => {
        // 4 semanas (aprox) retroativas
        const now = new Date();
        const weeks = [0, 1, 2, 3].map((w) => {
            const end = new Date(now);
            end.setDate(now.getDate() - w * 7);
            const start = new Date(end);
            start.setDate(end.getDate() - 7);

            const startTs = start.getTime();
            const endTs = end.getTime();

            const total = orders
                .filter((o) => (o.createdAt || 0) >= startTs && (o.createdAt || 0) < endTs)
                .reduce((sum, o) => sum + Number(o.total || 0), 0);

            return { name: `Semana ${4 - w}`, value: Math.round(total) };
        });

        return weeks;
    }, [orders]);

    const lastOrders = useMemo(() => {
        return [...orders].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 8);
    }, [orders]);

    function formatDate(ts) {
        if (!ts) return "-";
        const d = new Date(ts);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }

    function paymentLabel(p) {
        if (!p?.method) return "N/A";
        return p.method === "card" ? "Cartão" : "PIX";
    }

    return (
        <Box sx={{ p: 4, ml: 32}}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                Dashboard – Visão Geral
            </Typography>

            {/* CARDS */}
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {stats.map((c, i) => (
                    <StatsCard key={i} label={c.label} value={c.value} />
                ))}
            </Box>

            {/* GRÁFICOS */}
            <Box sx={{ display: "flex", gap: 3, mt: 4, flexWrap: "wrap" }}>
                <Box sx={{ flex: 2, minWidth: 420 }}>
                    <ChartArea title="Receita da Semana" data={vendasSemana} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 320 }}>
                    <ChartPie title="Produtos Mais Vendidos" data={produtosVendidos} />
                </Box>
            </Box>

            <Box sx={{ mt: 4 }}>
                <ChartBar title="Faturamento por Semana" data={faturamentoMes} />
            </Box>

            {/* TABELA DE PEDIDOS */}
            <Paper sx={{ p: 3, borderRadius: 3, mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Últimos Pedidos
                </Typography>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Pagamento</TableCell>
                                    <TableCell>Data</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lastOrders.map((o) => (
                                    <TableRow key={o.id} hover>
                                        <TableCell sx={{ fontWeight: 700 }}>{o.id}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={getOrderStatusLabelPt(o.status)}
                                                color={getOrderStatusColor(o.status)}
                                            />
                                        </TableCell>
                                        <TableCell>{paymentLabel(o.payment)}</TableCell>
                                        <TableCell>{formatDate(o.createdAt)}</TableCell>
                                        <TableCell align="right">
                                            {Number(o.total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                component={Link}
                                                to={`/admin/pedidos/${o.id}`}
                                            >
                                                Acessar pedido
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {lastOrders.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography align="center" sx={{ py: 2 }}>
                                                Nenhum pedido encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
}
